import type {
  CompanyConfiguration,
  CompanyLogoUploadInput,
  MediaResourceModel,
  CreateCompanyInput,
  UpdateCompanyInput,
} from 'src/core/data/api/CompanyConfiguration';
import BaseService from './base-service';

/**
 * Service for company configuration operations.
 * Handles company settings, theming, and logo management.
 */
export default class CompanyService extends BaseService {
  constructor() {
    super('company');
  }

  /**
   * Retrieves all company configurations.
   * @returns Promise resolving to array of company configurations
   */
  listCompanies(): Promise<CompanyConfiguration[]> {
    return this.list();
  }

  /**
   * Resolves a company configuration by full hostname.
   * The backend matches against customDomain first, then falls back to label.
   * @param hostname - The full hostname (e.g. "mycompany.monitorando.me" or "www.foo.com.br")
   * @returns Promise resolving to the company configuration, or null if not found
   */
  getByHostname(hostname: string): Promise<CompanyConfiguration | null> {
    return this.__get(`company/configurations/resolve/${hostname}`);
  }

  /**
   * Configuração da central que o usuário logado está vinculado -- quem manda é a central no topo
   * da cadeia, não o customer direto do usuário. Requer sessão; sem vínculo nenhum, o backend
   * devolve 404.
   * @returns Promise resolving to the company configuration, or null if the user has no central
   * with a configuration of its own.
   */
  getMine(): Promise<CompanyConfiguration | null> {
    return this.__get('company/configurations/mine');
  }

  /**
   * Retrieves a company configuration by its label.
   * @deprecated Use getByHostname instead. Will be removed in a future version.
   * @param label - The label/slug identifying the company configuration
   * @returns Promise resolving to the company configuration
   */
  getByLabel(label: string): Promise<CompanyConfiguration | null> {
    console.warn('[CompanyService] getByLabel is deprecated. Use getByHostname instead.');
    return this.__get(`company/configurations/${label}`);
  }

  /**
   * Creates a new company configuration.
   * @param data - The company configuration data
   * @returns Promise resolving to the created company configuration
   */
  createCompany(data: CreateCompanyInput): Promise<CompanyConfiguration> {
    return this.save(data);
  }

  /**
   * Updates an existing company configuration.
   * @param data - The company configuration data with id
   * @returns Promise resolving to the updated company configuration
   */
  updateCompany(data: UpdateCompanyInput): Promise<CompanyConfiguration> {
    return this.update(data);
  }

  /**
   * Deletes a company configuration by id.
   * @param id - The company configuration id
   * @returns Promise resolving when deleted
   */
  deleteCompany(id: number): Promise<void> {
    return this.remove(id);
  }

  /**
   * Uploads a company logo.
   * @param data - FormData containing the file and name
   * @returns Promise resolving to the media resource model with the logo URL
   */
  upload(data: FormData): Promise<MediaResourceModel> {
    return this.__post('company/configurations/upload', data);
  }

  /**
   * Helper to create FormData for logo upload.
   * @param input - The upload input containing file and name
   * @returns FormData ready for upload
   */
  static createUploadFormData(input: CompanyLogoUploadInput): FormData {
    const formData = new FormData();
    formData.append('attachment', input.attachment);
    formData.append('name', input.name);
    return formData;
  }
}
